import { SearchEntityType } from "@/models/search/Search";
import {
  SearchResult,
  LegislationSearchResult,
  WorkspaceDocumentSearchResult,
} from "@/models/search/SearchResults";
import SearchResultItem from "../search-result/SearchResultItem";
import SearchItemSkeleton from "../search-result/SearchItemSkeleton";
import { getCategoryTitle } from "../../constants/entityConfig";
import { ChevronRight, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";

interface GlobalSearchCategorySectionProps {
  entityType: SearchEntityType;
  results?: (LegislationSearchResult | WorkspaceDocumentSearchResult)[];
  isLoading?: boolean;
  hasMore?: boolean;
  isExpanded?: boolean;
  onExpand?: () => void;
  onBack?: () => void;
  maxResults?: number;
  lastElementRef?: (node: HTMLDivElement | null) => void;
  onResultClick?: () => void;
  hideExpandButton?: boolean; // When true, the expand button is not rendered (caller handles it)
  noMargin?: boolean; // When true, removes bottom margin (used in overview mode)
  totalRecords?: number; // Actual total count from backend (for displaying "X af Y")
}

const GlobalSearchCategorySection = ({
  entityType,
  results,
  isLoading = false,
  hasMore = false,
  isExpanded = false,
  onExpand,
  onBack,
  maxResults = 5,
  lastElementRef,
  onResultClick,
  hideExpandButton = false,
  noMargin = false,
  totalRecords,
}: GlobalSearchCategorySectionProps) => {
  // Convert typed results back to SearchResult format for SearchResultItem
  const convertToSearchResult = (
    result: LegislationSearchResult | WorkspaceDocumentSearchResult
  ): SearchResult => {
    if ("legislationExternalId" in result || "htmlAvailable" in result) {
      return { legislation: result as LegislationSearchResult };
    }
    if ("workspaceId" in result && "documentType" in result) {
      return { workspaceDocument: result as WorkspaceDocumentSearchResult };
    }
    return {};
  };

  // Loading state with no results yet
  if (isLoading && (!results || results.length === 0)) {
    return (
      <div className={cn(!noMargin && "mb-4")}>
        <div
          className={cn(
            "flex items-center justify-between py-2 px-2",
            isExpanded &&
              "sticky top-0 bg-background z-10 border-b border-border"
          )}
        >
          <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-1">
            {isExpanded && onBack && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={onBack}
                    className="p-1 -ml-1 hover:bg-accent hover:text-accent-foreground rounded-sm transition-colors"
                    aria-label="Tilbage til alle resultater"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  Tilbage til alle resultater
                </TooltipContent>
              </Tooltip>
            )}
            {getCategoryTitle(entityType)}
          </h3>
        </div>
        <SearchItemSkeleton hasCategory={false} />
        <SearchItemSkeleton hasCategory={false} />
      </div>
    );
  }

  // No results
  if (!results || results.length === 0) {
    return (
      <div className={cn(!noMargin && "mb-4")}>
        <div
          className={cn(
            "flex items-center justify-between py-2 px-2",
            isExpanded &&
              "sticky top-0 bg-background z-10 border-b border-border"
          )}
        >
          <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-1">
            {isExpanded && onBack && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={onBack}
                    className="p-1 -ml-1 hover:bg-accent hover:text-accent-foreground rounded-sm transition-colors"
                    aria-label="Tilbage til alle resultater"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  Tilbage til alle resultater
                </TooltipContent>
              </Tooltip>
            )}
            {getCategoryTitle(entityType)}
          </h3>
        </div>
        <div className="text-sm text-muted-foreground flex items-center justify-center p-4">
          Ingen resultater fundet
        </div>
      </div>
    );
  }

  // Determine which results to show
  const displayResults = isExpanded ? results : results.slice(0, maxResults);
  const totalCount = results.length;
  const showExpandButton = !isExpanded && (hasMore || totalCount > maxResults);

  return (
    <div className={cn(!noMargin && "mb-4")}>
      {/* Header */}
      <div
        className={cn(
          "flex items-center justify-between py-2 px-2",
          isExpanded && "sticky top-0 bg-background z-10 border-b border-border"
        )}
      >
        <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-1">
          {isExpanded && onBack && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={onBack}
                  className="p-1 -ml-1 hover:bg-accent hover:text-accent-foreground rounded-sm transition-colors"
                  aria-label="Tilbage til alle resultater"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                Tilbage til alle resultater
              </TooltipContent>
            </Tooltip>
          )}
          {getCategoryTitle(entityType)}
          {totalCount > 0 && (
            <span className="ml-2 text-xs text-muted-foreground/70">
              (
              {isExpanded
                ? (totalRecords ?? totalCount)
                : `${Math.min(maxResults, totalCount)} af ${totalRecords ?? totalCount}`}
              )
            </span>
          )}
        </h3>
      </div>

      {/* Results */}
      <div className="flex flex-col">
        {displayResults.map((result, index) => {
          const searchResult = convertToSearchResult(result);
          const resultId =
            (result as LegislationSearchResult).id ||
            (result as WorkspaceDocumentSearchResult).id ||
            index;

          const isLast = index === displayResults.length - 1;

          return (
            <SearchResultItem
              key={`${entityType}-${resultId}`}
              result={searchResult}
              isLast={isLast && isExpanded}
              lastElementRef={isLast && isExpanded ? lastElementRef : undefined}
              onResultClick={onResultClick}
            />
          );
        })}
      </div>

      {/* Loading more indicator */}
      {isLoading && isExpanded && results.length > 0 && (
        <div className="py-2">
          <SearchItemSkeleton hasCategory={false} />
        </div>
      )}

      {/* Expand button */}
      {showExpandButton && !hideExpandButton && (
        <button
          onClick={onExpand}
          className={cn(
            "w-full flex items-center justify-center gap-1 py-2 px-3",
            "text-sm text-primary hover:text-accent-foreground hover:bg-accent rounded-sm",
            "transition-colors cursor-pointer"
          )}
        >
          <span>Se flere resultater</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default GlobalSearchCategorySection;
